/**
 * planEmpty — shared predicate for "is the editable plan empty?"
 * (issue #498).
 *
 * The SLP must save a plan that has at least one piece of content
 * in any of the editable fields; an all-empty plan is treated as
 * "no plan yet" so the parent Save button is disabled and the
 * `Plan is empty` tooltip is surfaced. The predicate is also used
 * by `PlanEditor` to gate downstream behaviour (e.g. disabling
 * auto-save on a brand-new empty plan), so it lives here as a
 * single source of truth instead of being duplicated across the
 * editor and the parent screen.
 *
 * The shape matches `PlanEditorValue` from `PlanEditor.tsx`; the
 * interface is redeclared locally so this module has no React
 * dependency and can be imported from either the editor or the
 * screen without a circular import.
 */
export interface PlanEmptyInput {
  warmup: string;
  activities: { name: string; duration_min: number; cues: string; materials: string }[];
  prompts: string[];
  closing: string;
  parentUpdate: string;
}

/**
 * isPlanEmpty — true when every editable field is blank/whitespace.
 *
 * Whitespace-only fields count as empty (the SLP may have typed a
 * space and then backspaced, or pasted a blank line).
 */
export function isPlanEmpty(plan: PlanEmptyInput): boolean {
  return (
    !plan.warmup.trim() &&
    !plan.closing.trim() &&
    !plan.parentUpdate.trim() &&
    plan.activities.every(
      (a) => !a.name.trim() && !a.cues.trim() && !a.materials.trim(),
    ) &&
    plan.prompts.every((p) => !p.trim())
  );
}